import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart } from "lucide-react";

const PHOTOS = [
  { src: "/photos/heart-1.webp", pos: "center 45%" },
  { src: "/photos/heart-2.webp", pos: "center 55%" },
  { src: "/photos/heart-3.webp", pos: "center 45%" },
  { src: "/photos/heart-4.webp", pos: "center 40%" },
  { src: "/photos/heart-5.webp", pos: "center 50%" },
  { src: "/photos/heart-6.jpg", pos: "center 45%" },
  { src: "/photos/heart-7.webp", pos: "center 55%" },
  { src: "/photos/heart-8.webp", pos: "center 50%" },
  { src: "/photos/heart-9.webp", pos: "center 62%" },
  { src: "/photos/heart-10.webp", pos: "center 68%" },
  { src: "/photos/heart-11.webp", pos: "center 55%" },
  { src: "/photos/heart-12.webp", pos: "center 45%" },
];

const HEART_MASK =
  'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'%3E%3Cpath d=\'M50 90 C18 62 4 42 12 25 C19 11 37 10 50 27 C63 10 81 11 88 25 C96 42 82 62 50 90 Z\'/%3E%3C/svg%3E")';

const maskStyle = {
  WebkitMaskImage: HEART_MASK,
  maskImage: HEART_MASK,
  WebkitMaskSize: "contain",
  maskSize: "contain",
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
  WebkitMaskPosition: "center",
  maskPosition: "center",
};

export default function HeartPhotos() {
  const [index, setIndex] = useState(0);
  const [bursts, setBursts] = useState([]);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % PHOTOS.length), 3000);
    return () => clearInterval(t);
  }, []);

  const burst = () => {
    const id = Date.now() + Math.random();
    const particles = Array.from({ length: 16 }, (_, i) => ({
      id: `${id}-${i}`,
      x: (Math.random() - 0.5) * 300,
      y: 140 + Math.random() * 260,
      delay: Math.random() * 0.35,
      duration: 1.5 + Math.random() * 0.9,
      size: 12 + Math.random() * 18,
      rotate: (Math.random() - 0.5) * 160,
      color: Math.random() > 0.35 ? "#C86D51" : "#6E7E65",
    }));
    setBursts((b) => [...b, { id, particles }]);
    setTimeout(() => setBursts((b) => b.filter((x) => x.id !== id)), 2800);
  };

  return (
    <div
      className="relative w-[336px] h-[336px] sm:w-[360px] sm:h-[360px] cursor-pointer select-none"
      data-testid="heart-photo-frame"
      onClick={burst}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") burst(); }}
      aria-label="Tocca il cuore per una pioggia di cuoricini"
    >
      <motion.div
        key={bursts.length}
        animate={bursts.length ? { scale: [1, 1.06, 1] } : false}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="absolute inset-0"
      >
        <div className="absolute -inset-2 bg-terracotta" style={maskStyle} aria-hidden="true" />
        <div className="absolute inset-0 bg-panna" style={maskStyle}>
          {PHOTOS.map((p, i) => (
            <motion.img
              key={p.src}
              src={p.src}
              alt={`Federica e Alessandro — foto ${i + 1}`}
              initial={false}
              animate={{ opacity: i === index ? 1 : 0 }}
              transition={{ duration: 0.9, ease: "easeInOut" }}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: p.pos }}
              data-testid={i === index ? "heart-photo" : undefined}
            />
          ))}
        </div>
      </motion.div>

      {bursts.map((b) =>
        b.particles.map((p) => (
          <motion.span
            key={p.id}
            className="absolute pointer-events-none z-30"
            style={{ left: "50%", top: "42%", marginLeft: -p.size / 2, marginTop: -p.size / 2 }}
            initial={{ x: 0, y: 0, opacity: 0, scale: 0, rotate: 0 }}
            animate={{ x: p.x, y: p.y, opacity: [0, 1, 1, 0], scale: [0, 1, 1, 0.5], rotate: p.rotate }}
            transition={{ duration: p.duration, delay: p.delay, ease: "easeIn", times: [0, 0.15, 0.7, 1] }}
            data-testid="heart-particle"
          >
            <Heart size={p.size} fill={p.color} color={p.color} strokeWidth={0} />
          </motion.span>
        ))
      )}
    </div>
  );
}
